/**
 * SettingsPanel - Panel de configuración completo para personalización de navegación
 * Adaptado de NewLnF/components/SettingsPanel.tsx para AdminConsole (JavaScript + MUI)
 */

import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Paper,
  IconButton,
  Divider,
  Chip,
  Radio,
  RadioGroup,
  FormControl,
  Stack,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Palette as PaletteIcon,
  RestartAlt as RestartAltIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { useAppRegistryStore } from "../../stores/appRegistryStore";
import "./SettingsPanel.css";
import { useTranslation } from 'react-i18next';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsPanel = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation('common');
  const store = useAppRegistryStore();
  const [activeTab, setActiveTab] = useState(0);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState("#757575");
  
  // Estado para diálogo de confirmación
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  
  // Detectar modo oscuro
  const isDarkMode = store.settings.currentTheme === "dark";

  // Paleta de colores para selección rápida
  const colorPalette = [
    "#5C6BC0",
    "#00ACC1",
    "#AB47BC",
    "#00796B",
    "#757575",
    "#E53935",
    "#FB8C00",
    "#43A047",
    "#8E24AA",
    "#1E88E5",
    "#00897B",
    "#FFA726",
  ];

  const layoutOptions = [
    {
      id: "horizontal",
      name: i18n.language?.startsWith('en') ? "Horizontal Top Navigation" : "Navegación Superior Horizontal",
      description: i18n.language?.startsWith('en') ? "Navigation bar positioned at the top of the screen" : "Barra de navegación posicionada en la parte superior de la pantalla",
      bestPractice:
        i18n.language?.startsWith('en') ? "Best for apps with few main sections and a clean, spacious look. Ideal for desktop applications with 3-7 primary navigation items." : "Mejor para apps con pocas secciones principales y una apariencia limpia y espaciosa. Ideal para aplicaciones de escritorio con 3-7 elementos de navegación principales.",
    },
    {
      id: "vertical",
      name: i18n.language?.startsWith('en') ? "Vertical Side Navigation" : "Navegación Lateral Vertical",
      description: i18n.language?.startsWith('en') ? "Navigation sidebar positioned on the left side" : "Barra de navegación posicionada en el lado izquierdo",
      bestPractice:
        i18n.language?.startsWith('en') ? "Best for apps with many sections or nested menus. Keeps navigation accessible at all times and supports complex hierarchies." : "Mejor para apps con muchas secciones o menús anidados. Mantiene la navegación accesible en todo momento y soporta jerarquías complejas.",
    },
    {
      id: "auto-detect",
      name: i18n.language?.startsWith('en') ? "Auto-Detect Layout (Recommended)" : "Detección Automática de Layout (Recomendado)",
      description: i18n.language?.startsWith('en') ? "Automatically adapts based on screen size and device type" : "Se adapta automáticamente según el tamaño de pantalla y el tipo de dispositivo",
      bestPractice:
        i18n.language?.startsWith('en') ? "Automatically applies Horizontal on larger screens (desktop) and Vertical on smaller screens (tablet/mobile). Provides optimal UX across all devices." : "Aplica Horizontal en pantallas grandes (escritorio) y Vertical en pantallas pequeñas (tablet/móvil). Proporciona una UX óptima en todos los dispositivos.",
    },
  ];

  const handleAddCategory = () => {
    if (newCategoryName.trim()) {
      const newCategory = {
        id: `custom-${Date.now()}`,
        name: newCategoryName.trim(),
        color: newCategoryColor,
      };
      store.addCategory(newCategory);
      setNewCategoryName("");
      setNewCategoryColor("#757575");
    }
  };

  const handleRenameCategory = (categoryId, newName) => {
    if (newName.trim()) {
      store.renameCategory(categoryId, newName.trim());
      setEditingCategory(null);
    }
  };

  const handleDeleteCategory = (categoryId) => {
    setConfirmDialog({
      open: true,
      title: "Eliminar Categoría",
      message: '¿Estás seguro de que quieres eliminar esta categoría? Las apps se moverán a "Sin categoría".',
      onConfirm: () => {
        store.removeCategory(categoryId);
        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
      },
    });
  };

  const handleCategoryColorChange = (categoryId, color) => {
    store.setCategoryColor(categoryId, color);
  };

  const handleAppColorChange = (appId, color) => {
    store.setAppColor(appId, color);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Estilos de tarjetas internas (cajas blancas con esquinas redondeadas)
  const paperStyles = {
    backgroundColor: "#ffffff",
    borderColor: "#e0e0e0",
    borderRadius: 2,
    boxShadow: 3,
  };

  // Helper para estilos de Typography en modo oscuro
  const typographyStyles = {
    body1: { color: isDarkMode ? "#ffffff" : "#1a1a1a" },
    body2: { color: isDarkMode ? "#e0e0e0" : "text.secondary" },
    h6: { color: isDarkMode ? "#ffffff" : "#1a1a1a" },
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth 
      className={isDarkMode ? "dark" : ""}
      PaperProps={{ 
        sx: { 
          height: "90vh",
          backgroundColor: 'var(--bg-primary)',
          color: 'var(--text-primary)',
          borderRadius: 2,
          boxShadow: 12,
        } 
      }}
    >
      <DialogTitle 
        sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          pb: 1,
          backgroundColor: 'var(--bg-primary)',
          color: 'var(--text-primary)',
        }}
      >
        <Typography variant="h5" component="div" sx={{ color: 'var(--text-primary)' }}>
          {t('settingsPanel.title')}
        </Typography>
        <IconButton 
          onClick={onClose} 
          size="small"
          sx={{ color: 'var(--text-primary)' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box 
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider', 
          px: 3,
          backgroundColor: 'var(--bg-primary)',
        }}
      >
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="settings tabs"
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTab-root": {
              color: 'var(--text-primary)',
              fontWeight: 600,
              textTransform: 'uppercase',
              minHeight: 44,
            },
            "& .MuiTab-root.Mui-selected": {
              color: 'var(--text-primary)',
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#1976d2",
              height: 3,
              borderRadius: 2,
            },
          }}
        >
          <Tab label={t('settingsPanel.tabs.general')} />
          <Tab label={t('settingsPanel.tabs.categories')} />
          <Tab label={t('settingsPanel.tabs.colors')} />
          <Tab label={t('settingsPanel.tabs.layout')} />
        </Tabs>
      </Box>

      <DialogContent 
        dividers 
        sx={{ 
          p: 0, 
          overflow: "auto",
          backgroundColor: 'var(--bg-primary)',
          color: 'var(--text-primary)',
        }}
      >
        {/* Configuración General */}
        <TabPanel value={activeTab} index={0}>
          <Stack spacing={3} sx={{ backgroundColor: 'var(--bg-primary)' }}>
            <Box>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ color: 'var(--text-primary)' }}
              >
                {t('settingsPanel.displayPreferences')}
              </Typography>

              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2, ...paperStyles }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={store.settings.topBarIconStyle === "colored"}
                        onChange={() => store.toggleTopBarIconStyle()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ color: '#1a1a1a' }}>
                          {t('settingsPanel.general.topBarIconStyleTitle')}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ color: 'text.secondary' }}
                        >
                          {store.settings.topBarIconStyle === "colored"
                            ? t('settingsPanel.general.topBarIconStyleDescColored')
                            : t('settingsPanel.general.topBarIconStyleDescMinimal')}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, ...paperStyles }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={store.settings.showAppNameLabels}
                        onChange={() => store.toggleAppNameLabels()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={typographyStyles.body1}>
                          {t('settingsPanel.general.showAppNameLabelsTitle')}
                        </Typography>
                        <Typography variant="body2" sx={typographyStyles.body2}>
                          {t('settingsPanel.general.showAppNameLabelsDesc')}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, ...paperStyles }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={store.settings.showHoverTooltips}
                        onChange={() => store.toggleHoverTooltips()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={typographyStyles.body1}>
                          {t('settingsPanel.general.showHoverTooltipsTitle')}
                        </Typography>
                        <Typography variant="body2" sx={typographyStyles.body2}>
                          {t('settingsPanel.general.showHoverTooltipsDesc')}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, ...paperStyles }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={store.settings.showStarShortcut}
                        onChange={() => store.toggleStarShortcut()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={typographyStyles.body1}>
                          {t('settingsPanel.general.showStarShortcutTitle')}
                        </Typography>
                        <Typography variant="body2" sx={typographyStyles.body2}>
                          {t('settingsPanel.general.showStarShortcutDesc')}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, ...paperStyles }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={store.settings.showAppLauncher}
                        onChange={() => store.toggleAppLauncher()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={typographyStyles.body1}>
                          {t('settingsPanel.general.showAppLauncherTitle')}
                        </Typography>
                        <Typography variant="body2" sx={typographyStyles.body2}>
                          {t('settingsPanel.general.showAppLauncherDesc')}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, ...paperStyles }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={store.settings.showSearchBox}
                        onChange={() => store.toggleSearchBox()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={typographyStyles.body1}>
                          {t('settingsPanel.general.showSearchBoxTitle')}
                        </Typography>
                        <Typography variant="body2" sx={typographyStyles.body2}>
                          {t('settingsPanel.general.showSearchBoxDesc')}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, ...paperStyles }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={store.settings.showCommandPalette}
                        onChange={() => store.toggleCommandPalette()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={typographyStyles.body1}>
                          {t('settingsPanel.general.showCommandPaletteTitle')}
                        </Typography>
                        <Typography variant="body2" sx={typographyStyles.body2}>
                          {t('settingsPanel.general.showCommandPaletteDesc')}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, ...paperStyles }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={store.settings.showHomeDashboard}
                        onChange={() => store.toggleHomeDashboard()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={typographyStyles.body1}>
                          {t('settingsPanel.general.showHomeDashboardTitle')}
                        </Typography>
                        <Typography variant="body2" sx={typographyStyles.body2}>
                          {t('settingsPanel.general.showHomeDashboardDesc')}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                  />
                </Paper>

                {store.settings.showHomeDashboard && (
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      bgcolor: isDarkMode ? "#2d2d2d" : "action.hover",
                      backgroundColor: isDarkMode ? "#2d2d2d" : undefined,
                      borderColor: isDarkMode ? "#404040" : "#e0e0e0",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={store.settings.dashboardDisplayMode === "frequent"}
                          onChange={() => store.toggleDashboardMode()}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" sx={typographyStyles.body1}>
                            {t('settingsPanel.general.dashboardModeTitle')}
                          </Typography>
                          <Typography variant="body2" sx={typographyStyles.body2}>
                            {store.settings.dashboardDisplayMode === "pinned"
                              ? t('settingsPanel.general.dashboardModePinned')
                              : t('settingsPanel.general.dashboardModeFrequent')}
                          </Typography>
                        </Box>
                      }
                      labelPlacement="start"
                      sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                    />
                  </Paper>
                )}

                <Paper variant="outlined" sx={{ p: 2, ...paperStyles }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={store.settings.showFontSizeControl}
                        onChange={() => store.toggleFontSizeControl()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={typographyStyles.body1}>
                          {t('settingsPanel.general.fontSizeControlTitle')}
                        </Typography>
                        <Typography variant="body2" sx={typographyStyles.body2}>
                          {t('settingsPanel.general.fontSizeControlDesc')}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, ...paperStyles }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={store.settings.showLanguageSelector}
                        onChange={() => store.toggleLanguageSelector()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={typographyStyles.body1}>
                          {t('settingsPanel.general.languageSelectorTitle')}
                        </Typography>
                        <Typography variant="body2" sx={typographyStyles.body2}>
                          {t('settingsPanel.general.languageSelectorDesc')}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, ...paperStyles }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={store.settings.showFullscreenToggle}
                        onChange={() => store.toggleFullscreenToggle()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={typographyStyles.body1}>
                          {t('settingsPanel.general.fullscreenToggleTitle')}
                        </Typography>
                        <Typography variant="body2" sx={typographyStyles.body2}>
                          {t('settingsPanel.general.fullscreenToggleDesc')}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, ...paperStyles }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={store.settings.navigationMode === "groups"}
                        onChange={() => store.toggleModuleGroups()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={typographyStyles.body1}>
                          {t('settingsPanel.general.moduleGroupsTitle')}
                        </Typography>
                        <Typography variant="body2" sx={typographyStyles.body2}>
                          {t('settingsPanel.general.moduleGroupsDesc')}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="start"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                  />
                </Paper>

                {store.settings.navigationMode === "groups" && (
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      bgcolor: isDarkMode ? "#2d2d2d" : "action.hover",
                      backgroundColor: isDarkMode ? "#2d2d2d" : undefined,
                      borderColor: isDarkMode ? "#404040" : "#e0e0e0",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={store.settings.showGroupNames}
                          onChange={() => store.toggleGroupNames()}
                        />
                      }
                      label={
                        <Box>
                        <Typography variant="body1" sx={typographyStyles.body1}>
                          {t('settingsPanel.general.groupNamesTitle')}
                        </Typography>
                        <Typography variant="body2" sx={typographyStyles.body2}>
                          {t('settingsPanel.general.groupNamesDesc')}
                        </Typography>
                        </Box>
                      }
                      labelPlacement="start"
                      sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                    />
                  </Paper>
                )}
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom>
                {t('settingsPanel.general.resetOptions')}
              </Typography>
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<RestartAltIcon />}
                  onClick={() => {
                    setConfirmDialog({
                      open: true,
                      title: "Reiniciar Categorías",
                      message: "¿Reiniciar todas las categorías a valores por defecto? Esto eliminará categorías personalizadas y restaurará nombres originales.",
                      onConfirm: () => {
                        store.resetCategoriesToDefault();
                        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
                      },
                    });
                  }}
                  fullWidth
                  sx={{
                    justifyContent: "flex-start",
                    bgcolor: "#ffffff",
                    borderColor: "#e0e0e0",
                    color: isDarkMode ? "#ffffff" : "#1a1a1a",
                    '&:hover': {
                      bgcolor: '#f9f9f9',
                    },
                  }}
                >
                  {t('settingsPanel.general.resetCategoriesButton')}
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<PaletteIcon />}
                  onClick={() => {
                    setConfirmDialog({
                      open: true,
                      title: "Reiniciar Colores",
                      message: "¿Reiniciar todos los colores a valores por defecto? Esto eliminará todas las sobrescrituras de color personalizadas.",
                      onConfirm: () => {
                        store.resetColorsToDefault();
                        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
                      },
                    });
                  }}
                  fullWidth
                  sx={{
                    justifyContent: "flex-start",
                    bgcolor: "#ffffff",
                    borderColor: "#e0e0e0",
                    color: isDarkMode ? "#ffffff" : "#1a1a1a",
                    '&:hover': {
                      bgcolor: '#f9f9f9',
                    },
                  }}
                >
                  {t('settingsPanel.general.resetColorsButton')}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </TabPanel>

        {/* Gestión de Categorías */}
        <TabPanel value={activeTab} index={1}>
          <Stack spacing={3} sx={{ backgroundColor: 'var(--bg-primary)' }}>
            <Paper variant="outlined" sx={{ p: 2, ...paperStyles }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'var(--text-primary)' }}>
                {i18n.language?.startsWith('en') ? 'Add New Category' : 'Agregar Nueva Categoría'}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <TextField
                  placeholder={i18n.language?.startsWith('en') ? 'Category name' : 'Nombre de categoría'}
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  fullWidth
                  size="small"
                />
                <input
                  type="color"
                  value={newCategoryColor}
                  onChange={(e) => setNewCategoryColor(e.target.value)}
                  style={{ width: 40, height: 40, borderRadius: 4, border: "1px solid #ccc" }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddCategory}
                  disabled={!newCategoryName.trim()}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Add
                </Button>
              </Box>
            </Paper>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'var(--text-primary)' }}>
                Existing Categories
              </Typography>
              <Stack spacing={2}>
                {store.categories.map((category) => {
                  const appsInCategory = store.apps.filter((app) => app.categoryId === category.id);
                  const effectiveColor = (store.settings.categoryColors && store.settings.categoryColors[category.id]) || category.color;

                  return (
                    <Paper key={category.id} variant="outlined" sx={{ p: 2, ...paperStyles }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Box sx={{ width: 24, height: 24, bgcolor: effectiveColor, borderRadius: 1 }} />
                          {editingCategory === category.id ? (
                            <TextField
                              defaultValue={category.name}
                              onBlur={(e) => handleRenameCategory(category.id, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleRenameCategory(category.id, e.target.value);
                                } else if (e.key === "Escape") {
                                  setEditingCategory(null);
                                }
                              }}
                              autoFocus
                              size="small"
                              sx={{ width: 200 }}
                            />
                          ) : (
                            <Box>
                              <Typography variant="body1" fontWeight="medium">
                                {category.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {appsInCategory.length} apps
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton size="small" onClick={() => setEditingCategory(category.id)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteCategory(category.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        </TabPanel>

        {/* Personalización de Colores */}
        <TabPanel value={activeTab} index={2}>
          <Stack spacing={3} sx={{ backgroundColor: 'var(--bg-primary)' }}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'var(--text-primary)' }}>
                {t('settingsPanel.colors.categoryColorsTitle')}
              </Typography>
              <Stack spacing={2}>
                {store.categories.map((category) => {
                  const currentColor = (store.settings.categoryColors && store.settings.categoryColors[category.id]) || category.color;

                  return (
                    <Paper key={category.id} variant="outlined" sx={{ p: 2, ...paperStyles }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Box sx={{ width: 24, height: 24, bgcolor: currentColor, borderRadius: 1 }} />
                          <Typography variant="body1" fontWeight="medium">
                            {category.name}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {/* Paleta de colores */}
                          {colorPalette.map((color) => (
                            <Box
                              key={color}
                              sx={{
                                width: 24,
                                height: 24,
                                bgcolor: color,
                                borderRadius: 1,
                                border: currentColor === color ? "2px solid #000" : "2px solid #ddd",
                                cursor: "pointer",
                                transform: currentColor === color ? "scale(1.1)" : "scale(1)",
                                transition: "all 0.2s",
                                "&:hover": {
                                  transform: "scale(1.15)",
                                },
                              }}
                              onClick={() => handleCategoryColorChange(category.id, color)}
                            />
                          ))}

                          {/* Selector de color personalizado */}
                          <input
                            type="color"
                            value={currentColor}
                            onChange={(e) => handleCategoryColorChange(category.id, e.target.value)}
                            style={{ width: 32, height: 32, borderRadius: 4, border: "1px solid #ccc" }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'var(--text-primary)' }}>
                {t('settingsPanel.colors.appColorOverridesTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('settingsPanel.colors.appColorOverridesDesc')}
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                <Stack spacing={1}>
                  {store.apps.map((app) => {
                    const currentColor = (store.settings.appColors && store.settings.appColors[app.id]) || "";
                    const category = store.categories.find((cat) => cat.id === app.categoryId);
                    const defaultColor = (store.settings.categoryColors && store.settings.categoryColors[app.categoryId]) || category?.color || "#757575";

                    return (
                      <Paper key={app.id} variant="outlined" sx={{ p: 1.5, ...paperStyles }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                bgcolor: currentColor || defaultColor,
                                borderRadius: 1,
                              }}
                            />
                            <Typography variant="body2">{app.name}</Typography>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <input
                              type="color"
                              value={currentColor || defaultColor}
                              onChange={(e) => handleAppColorChange(app.id, e.target.value)}
                              style={{ width: 24, height: 24, borderRadius: 4, border: "1px solid #ccc" }}
                            />
                            {currentColor && (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleAppColorChange(app.id, "")}
                              >
                                {t('settingsPanel.colors.reset')}
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>
            </Box>
          </Stack>
        </TabPanel>

        {/* Estilos de Layout */}
        <TabPanel value={activeTab} index={3}>
          <Stack spacing={3} sx={{ backgroundColor: 'var(--bg-primary)' }}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'var(--text-primary)' }}>
                {t('settingsPanel.layout.preferencesTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('settingsPanel.layout.preferencesDesc')}
              </Typography>

              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={store.settings.layoutStyle}
                  onChange={(e) => store.setLayoutStyle(e.target.value)}
                >
                  <Stack spacing={2}>
                    {layoutOptions.map((option) => (
                      <Paper
                        key={option.id}
                        variant="outlined"
                        sx={{
                          p: 2,
                          cursor: "pointer",
                          border: store.settings.layoutStyle === option.id ? 2 : 1,
                          borderColor: store.settings.layoutStyle === option.id ? "primary.main" : "#e0e0e0",
                          bgcolor: "#ffffff",
                          borderRadius: 2,
                          boxShadow: 3,
                          "&:hover": {
                            boxShadow: 6,
                            borderColor: "primary.light",
                          },
                        }}
                        onClick={() => store.setLayoutStyle(option.id)}
                      >
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                          <Radio value={option.id} sx={{ mt: 0.5 }} />
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                              <Typography variant="subtitle1" fontWeight="medium">
                                {option.name}
                              </Typography>
                              {option.id === "auto-detect" && (
                                <Chip label={t('settingsPanel.layout.recommended')} color="primary" size="small" />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {option.description}
                            </Typography>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                bgcolor: "#f7f7f7",
                                borderLeft: 2,
                                borderLeftColor: "primary.main",
                                borderRadius: 1,
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                <strong>{t('settingsPanel.layout.bestPractice')}</strong> {option.bestPractice}
                              </Typography>
                            </Paper>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                </RadioGroup>
              </FormControl>
            </Box>
          </Stack>
        </TabPanel>
      </DialogContent>

      <DialogActions 
        sx={{ 
          p: 2,
          backgroundColor: 'var(--bg-primary)',
          borderTop: '1px solid var(--border-color, #e0e0e0)',
        }}
      >
        <Button onClick={onClose} variant="contained" startIcon={<SaveIcon />}>
          {t('settingsPanel.save')}
        </Button>
      </DialogActions>
      
      {/* Diálogo de confirmación */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: "", message: "", onConfirm: null })}
        PaperProps={{
          sx: {
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
          },
        }}
      >
        <DialogTitle sx={{ color: 'var(--text-primary)' }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--text-primary)' }}>
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: 'var(--bg-primary)' }}>
          <Button
            onClick={() => setConfirmDialog({ open: false, title: "", message: "", onConfirm: null })}
            sx={{ color: 'var(--text-primary)' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.onConfirm) {
                confirmDialog.onConfirm();
              }
            }}
            variant="contained"
            color="primary"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default SettingsPanel;

